/* eslint-disable react/require-default-props */
import React, { useContext } from 'react';
import { useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Dropdown } from '@superops/ui-design-system';

import { AccountClient, GET_SERVICE_CATALOG_ITEMS } from '@nile/graphql';
import {
  DropdownIndicator,
  formatGroupLabel,
  IndicatorSeparator,
  Loader,
  reactSelectInlineError,
  translateText
} from '@nile/components';

import { ServiceCatalogFormContext } from '../ServiceCatalogForm';
import { ServiceCatalogItem, PackageTypeItemOfferingType } from '../types';

const offerItemTypeBasedCondition: Record<
  PackageTypeItemOfferingType,
  {
    attribute: string;
    operator: string;
    value: string | boolean;
  }[]
> = {
  BLOCK_HOUR: [
    {
      attribute: 'serviceTypeItem.offeringType',
      operator: 'is',
      value: 'HOURS'
    }
  ],
  BLOCK_MONEY: [
    {
      attribute: 'offerAsWorklogItem',
      operator: 'is',
      value: true
    }
  ],
  SERVICE_BUNDLE: [
    {
      attribute: 'serviceTypeItem.offeringType',
      operator: 'isNot',
      value: 'SERVICE_BUNDLE'
    },
    {
      attribute: 'serviceTypeItem.redeemableServiceBundle.applicableType',
      operator: 'isNot',
      value: 'ALL_ITEMS'
    }
  ]
};

function ServiceItemsDropdown({
  value = null,
  onChange,
  isMulti = false,
  className = 'w200',
  isDisabled,
  offerItemType,
  filterServiceItems = []
}: {
  value?: any;
  className?: string;
  isMulti?: boolean;
  isDisabled?: boolean;
  onChange: (val) => void;
  offerItemType: PackageTypeItemOfferingType;
  filterServiceItems: ServiceCatalogItem[];
}): React.ReactNode {
  const { errors } = useContext(ServiceCatalogFormContext);

  const { loading, data, error } = useQuery(GET_SERVICE_CATALOG_ITEMS, {
    client: AccountClient,
    fetchPolicy: 'cache-and-network',
    variables: {
      listInfo: {
        condition: offerItemTypeBasedCondition[offerItemType]?.length
          ? {
              joinOperator: 'AND',
              operands: [
                ...offerItemTypeBasedCondition[offerItemType],
                {
                  attribute: 'status',
                  operator: 'is',
                  value: 'ACTIVE'
                }
              ]
            }
          : null
      }
    }
  });

  const ServiceItemIdsToBeFiltered =
    filterServiceItems?.map((item) => item?.itemId) || [];

  let selectedValue = null;

  if (value) {
    selectedValue = isMulti
      ? data?.getServiceCatalogItems?.items?.filter((item) => {
          return value?.includes(item?.itemId);
        })
      : [
          data?.getServiceCatalogItems?.items?.find(
            (item) => item?.itemId === value
          )
        ];
  }

  return (
    <div className="fx actr g20">
      <div className={className}>
        {loading ? (
          <Loader />
        ) : (
          <Dropdown
            isClearable
            name="subscriptionTypes"
            isMulti={isMulti}
            value={
              selectedValue?.map((item) => {
                return {
                  ...item,
                  value: item?.itemId,
                  label: item?.name
                };
              }) || null
            }
            onChange={onChange}
            options={data?.getServiceCatalogItems?.items
              .filter((item) => {
                return !ServiceItemIdsToBeFiltered?.includes(item?.itemId);
              })
              ?.map((el) => {
                return {
                  ...el,
                  value: el.itemId,
                  label: el.name
                };
              })}
            components={{
              IndicatorSeparator,
              DropdownIndicator
            }}
            menuPlacement="auto"
            classNamePrefix="value_selector"
            menuPortalTarget={document.body}
            formatGroupLabel={formatGroupLabel}
            placeholder={translateText(
              'admin.serviceCatalog.chooseItems.placeholder'
            )}
            closeMenuOnScroll={(e) => {
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 })
            }}
            isDisabled={isDisabled}
          />
        )}
      </div>

      {errors?.['serviceTypeItem.bundledItems']?.[0] &&
        reactSelectInlineError(
          <FormattedMessage id="Need atleast one Service item." />
        )}
    </div>
  );
}

export default ServiceItemsDropdown;
